import { useEffect } from 'react';
import { ADSENSE_CLIENT, ADSENSE_SLOTS } from '../config';

declare global {
  interface Window {
    adsbygoogle?: object[];
  }
}

type AdSlotProps = {
  /** Which configured AdSense ad unit to render. */
  slot: keyof typeof ADSENSE_SLOTS;
  className?: string;
};

/**
 * One AdSense ad unit.
 *
 * Three states:
 *  - AdSense fully configured (client + slot id): renders the real
 *    responsive <ins> unit. Consent gating is handled upstream - Consent Mode
 *    v2 defaults in index.html plus Google's certified CMP decide what, if
 *    anything, actually serves.
 *  - Dev build without AdSense config: renders a labeled placeholder box so
 *    layout and CLS behavior are testable.
 *  - Production build without AdSense config: renders nothing.
 *
 * The wrapper reserves vertical space (min-h) so a late-loading ad cannot
 * shift the recipe content underneath it (CLS).
 */
export function AdSlot({ slot, className = '' }: AdSlotProps) {
  const slotId = ADSENSE_SLOTS[slot];
  const enabled = Boolean(ADSENSE_CLIENT && slotId);

  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // Blocked or failed loader - the empty slot collapses gracefully.
    }
  }, [enabled]);

  if (!enabled) {
    if (!import.meta.env.DEV) return null;
    return (
      <div
        className={`my-8 flex min-h-[120px] items-center justify-center rounded-lg border-2 border-dashed border-plume-200 bg-plume-50 text-sm text-plume-600 ${className}`}
        role="note"
        aria-label="Advertisement placeholder (development only)"
      >
        Ad slot “{slot}”: placeholder shown because VITE_ADSENSE_CLIENT is unset
      </div>
    );
  }

  return (
    <div className={`my-8 min-h-[120px] ${className}`}>
      <p className="mb-1 text-center text-[0.65rem] uppercase tracking-widest text-plume-600">
        Advertisement
      </p>
      <ins
        className="adsbygoogle block"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
